/**
 * Cross-property account hand-off.
 *
 * The AppCursor extension, sms.ulgebra.com and app.ulgebra.com each hold their
 * own signed-in session. Following a link between them can silently land the
 * user on a property signed in as a *different* account — they then install or
 * configure an app under the wrong one and only find out much later.
 *
 * So every hop carries the sender's account email in the URL, and the receiving
 * property compares it against whoever is signed in there.
 *
 * The param is `suggestLoginMail` — the same one the in-CRM app iframes already
 * pass (see AuthGuard and base-saas-module.getSuggestedLoginEmail), so the
 * receivers need no new convention and a hand-off URL still pre-fills the login
 * form on the way through.
 */

export const HANDOFF_EMAIL_PARAM = 'suggestLoginMail';

/**
 * Append the sender's account email to an outgoing URL.
 * Returns `url` untouched when there is no email to pass (signed out).
 */
export function withAccountHandoff(url: string, email?: string | null): string {
  if (!email) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${HANDOFF_EMAIL_PARAM}=${encodeURIComponent(email)}`;
}

/** The account the sender expected us to be signed in as, if any. */
export function readHandoffEmail(search: string = window.location.search): string | null {
  try {
    const params = new URLSearchParams(search);
    const email = params.get(HANDOFF_EMAIL_PARAM) || params.get('userEmail');
    return email ? email.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * `/login` URL that keeps a hand-off alive across the sign-in detour: carries
 * the expected account through so the form pre-fills it, and `nextpage` so the
 * user lands back where they were with the hand-off param still on the URL
 * (which is what lets the mismatch check re-run afterwards).
 *
 * Defaults `nextPage` to the current location — callers on the page being
 * returned to don't need to pass anything.
 */
export function handoffLoginUrl(
  expectedEmail?: string | null,
  nextPage: string = window.location.pathname + window.location.search,
): string {
  const params = new URLSearchParams();
  if (expectedEmail) params.set(HANDOFF_EMAIL_PARAM, expectedEmail);
  params.set('nextpage', nextPage);
  return `/login?${params.toString()}`;
}

/**
 * Drop the hand-off param from the address bar once it has served its purpose.
 *
 * It carries an email address, so leaving it behind puts a real user's address
 * into browser history, bookmarks, analytics page URLs, `Referer` headers on
 * outbound requests, and any link the user copies out of the address bar.
 *
 * Only safe to call once the mismatch is *resolved* — while it is still open
 * the param is what makes the warning survive a reload. `replaceState` keeps it
 * out of history and doesn't remount the router.
 */
export function stripHandoffParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(HANDOFF_EMAIL_PARAM) && !url.searchParams.has('userEmail')) return;
    url.searchParams.delete(HANDOFF_EMAIL_PARAM);
    url.searchParams.delete('userEmail');
    window.history.replaceState(null, '', url.toString());
  } catch { /* URL / history unavailable */ }
}

/** Key shared with AccountMismatchBanner so dismissing in one place sticks. */
const dismissKey = (currentEmail: string) =>
  `ua_ignore_account_mismatch_for_${currentEmail.toLowerCase()}`;

export function isMismatchDismissed(currentEmail: string, expectedEmail: string): boolean {
  try {
    return localStorage.getItem(dismissKey(currentEmail)) === expectedEmail.toLowerCase();
  } catch {
    return false;
  }
}

export function dismissMismatch(currentEmail: string, expectedEmail: string): void {
  try {
    localStorage.setItem(dismissKey(currentEmail), expectedEmail.toLowerCase());
  } catch { /* localStorage unavailable — banner reappears next load */ }
}
